<script setup>
/* global axios */
import {
  computed,
  reactive,
  ref,
  watch,
  onMounted,
  onUnmounted,
  nextTick,
} from 'vue';
import { useRoute, useRouter, onBeforeRouteLeave } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import TemplatesAPI from 'dashboard/api/messageTemplates';
import WabaTemplatesAPI from 'dashboard/api/wabaTemplates';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TemplatePageHeader from './templates/TemplatePageHeader.vue';
import PreparationEditor from './templates/PreparationEditor.vue';
import {
  appointmentMessages,
  appointmentPreview,
  tokenGroup,
} from './templates/appointmentMessages';
import ChannelPhonePreview from 'dashboard/components-next/message/preview/ChannelPhonePreview.vue';
import {
  messages,
  CHANNELS,
  TOKENS,
  SAMPLE,
  emptyVariant,
} from './templates/editor';

const { t } = useI18n({
  useScope: 'local',
  messages: {
    ru: { ...messages.ru, appointmentMessages: appointmentMessages.ru },
    en: { ...messages.en, appointmentMessages: appointmentMessages.en },
  },
});
const am = key => t(`appointmentMessages.${key}`);
const tokenGroups = [
  'patient',
  'appointment',
  'before',
  'after',
  'daily',
  'other',
].map(key => ({
  key,
  tokens: TOKENS.filter(token => tokenGroup(token) === key),
}));
const route = useRoute();
const router = useRouter();
const templates = ref([]);
const search = ref('');
const filteredTemplates = computed(() =>
  templates.value.filter(item =>
    `${item.name} ${item.category || ''}`
      .toLowerCase()
      .includes(search.value.trim().toLowerCase())
  )
);
const loading = ref(false);
const saving = ref(false);
const wabaTemplates = ref([]);
const editing = ref(false);
const activeChannel = ref('max');
const form = reactive({
  id: null,
  name: '',
  category: '',
  variants: {},
  enabled: {},
});
const saved = ref('');
const error = ref('');
const previewError = ref('');
const previews = ref({});
const previewBusy = ref(false);
const uploading = ref(false);
const bodyRef = ref(null);
const test = reactive({ to: '', inboxId: '', sending: false, result: '' });
const inboxes = ref([]);
const copyFrom = ref('');
const vars = reactive({ ...SAMPLE });
const variant = computed(() => form.variants[activeChannel.value]);
const example = ref('single');
const preparationSamples = reactive({});
const preparationFields = computed(() => [
  ...new Map(
    (variant.value?.preparation_rules || [])
      .filter(rule => rule.field)
      .map(rule => [`${rule.context}.${rule.field}`, rule])
  ).entries(),
]);
const previewVars = () =>
  appointmentPreview(
    vars,
    example.value,
    variant.value?.preparation_rules || [],
    preparationSamples
  );
const channelLabel = key => CHANNELS.find(c => c.key === key)?.label || key;
const normalizedVariants = () =>
  Object.fromEntries(
    CHANNELS.filter(c => form.enabled[c.key]).map(c => [
      c.key,
      JSON.parse(JSON.stringify(form.variants[c.key])),
    ])
  );
const payload = () => ({
  name: form.name.trim(),
  category: form.category,
  channel_variants: normalizedVariants(),
});
const snapshot = () => JSON.stringify(payload());
const dirty = computed(() => editing.value && snapshot() !== saved.value);
const preview = computed(() => previews.value[activeChannel.value]);
const wabaOptions = computed(() =>
  wabaTemplates.value
    .filter(
      item => item.status === 'approved' && item.available && item.supported
    )
    .map(item => item.binding)
);
const relevantInboxes = computed(() =>
  inboxes.value.filter(item =>
    (
      ({
        max: ['Channel::Max'],
        telegram: ['Channel::Telegram'],
        whatsapp: ['Channel::Wazzup'],
        sms: ['Channel::Smsc', 'Channel::Smsru', 'Channel::Exolve'],
      })[activeChannel.value] || []
    ).includes(item.channel_type)
  )
);
const canSend = computed(
  () =>
    form.id &&
    !dirty.value &&
    form.enabled[activeChannel.value] &&
    !previewBusy.value &&
    preview.value &&
    !preview.value.error &&
    !(preview.value.missing_variables || []).length &&
    !(preview.value.warnings || []).length &&
    test.inboxId &&
    test.to.trim()
);
const describeError = failure => {
  const data = failure.response?.data || {};
  if (data.variant_errors?.length)
    return data.variant_errors
      .map(item => `${channelLabel(item.channel)}: ${t('errors.' + item.code)}`)
      .join('\n');
  return data.errors?.join(', ') || data.error || t('failed');
};
const allowLeave = () => {
  // eslint-disable-next-line no-alert
  return !dirty.value || window.confirm(t('discard'));
};
onBeforeRouteLeave(allowLeave);
const beforeUnload = event => {
  if (dirty.value) {
    event.preventDefault();
    event.returnValue = '';
  }
};
let timer;
let sequence = 0;
const refreshPreview = () => {
  clearTimeout(timer);
  sequence += 1;
  const request = sequence;
  previews.value = {};
  previewError.value = '';
  if (!editing.value) return;
  previewBusy.value = true;
  timer = setTimeout(async () => {
    try {
      const { data } = await TemplatesAPI.preview({
        ...payload(),
        channels: CHANNELS.map(c => c.key),
        vars: previewVars(),
      });
      if (request === sequence) previews.value = data.variants || {};
    } catch (failure) {
      if (request === sequence) previewError.value = describeError(failure);
    } finally {
      if (request === sequence) previewBusy.value = false;
    }
  }, 300);
};
watch([() => form.variants, () => form.enabled, vars], refreshPreview, {
  deep: true,
});
watch([example, preparationSamples], refreshPreview, { deep: true });
watch(activeChannel, () => {
  test.inboxId = '';
  test.result = '';
  copyFrom.value = '';
  refreshPreview();
});
const edit = item => {
  if (!allowLeave()) return;
  const supplied = item?.channel_variants || {};
  Object.assign(form, {
    id: item?.id || null,
    name: item?.name || '',
    category: item?.category || '',
    variants: Object.fromEntries(
      CHANNELS.map(c => [
        c.key,
        {
          ...emptyVariant(),
          ...JSON.parse(JSON.stringify(supplied[c.key] || {})),
        },
      ])
    ),
    enabled: Object.fromEntries(CHANNELS.map(c => [c.key, !!supplied[c.key]])),
  });
  activeChannel.value = CHANNELS.find(c => form.enabled[c.key])?.key || 'max';
  editing.value = true;
  saved.value = snapshot();
  error.value = '';
  test.to = '';
  test.result = '';
  refreshPreview();
};
const openFromQuery = () => {
  const id = Number(route.query.template_id);
  if (id && id !== form.id) {
    const item = templates.value.find(row => row.id === id);
    if (item) edit(item);
  }
};
const load = async () => {
  loading.value = true;
  try {
    const { data } = await TemplatesAPI.get();
    templates.value = data || [];
    const catalog = await WabaTemplatesAPI.get();
    wabaTemplates.value = catalog.data.templates || [];
  } catch (failure) {
    error.value = describeError(failure);
  } finally {
    loading.value = false;
  }
};
onMounted(async () => {
  window.addEventListener('beforeunload', beforeUnload);
  await load();
  openFromQuery();
  try {
    const { data } = await axios.get(
      `/api/v1/accounts/${route.params.accountId}/inboxes`
    );
    inboxes.value = data.payload || [];
  } catch {
    /* Test sending remains unavailable without an exact source. */
  }
});
onUnmounted(() => {
  clearTimeout(timer);
  sequence += 1;
  window.removeEventListener('beforeunload', beforeUnload);
});
watch(() => route.query.template_id, openFromQuery);
const back = () => {
  if (!allowLeave()) return;
  editing.value = false;
  form.id = null;
  error.value = '';
  sequence += 1;
  clearTimeout(timer);
  if (route.query.template_id)
    router.replace({ query: { ...route.query, template_id: undefined } });
};
const save = async () => {
  if (saving.value || uploading.value) return;
  if (!form.name.trim()) {
    error.value = t('nameRequired');
    return;
  }
  saving.value = true;
  error.value = '';
  try {
    const { data } = form.id
      ? await TemplatesAPI.update(form.id, payload())
      : await TemplatesAPI.create(payload());
    form.id = data.id;
    saved.value = snapshot();
    templates.value = [
      ...templates.value.filter(item => item.id !== data.id),
      data,
    ].sort((a, b) => a.name.localeCompare(b.name));
    useAlert(t('saved'));
  } catch (failure) {
    error.value = describeError(failure);
  } finally {
    saving.value = false;
  }
};
const remove = async item => {
  // eslint-disable-next-line no-alert
  if (!window.confirm(t('deleteConfirm', { name: item.name }))) return;
  try {
    await TemplatesAPI.delete(item.id);
    await load();
  } catch (failure) {
    error.value = describeError(failure);
  }
};
const sync = async () => {
  router.push({
    name: 'automation_waba_templates_index',
    params: route.params,
  });
};
const insertToken = token => {
  const element = bodyRef.value;
  const insertion = `{{${token}}}`;
  const start = element?.selectionStart ?? variant.value.body.length;
  const end = element?.selectionEnd ?? start;
  variant.value.body =
    variant.value.body.slice(0, start) +
    insertion +
    variant.value.body.slice(end);
  nextTick(() => {
    element?.focus();
    if (element) {
      element.selectionStart = start + insertion.length;
      element.selectionEnd = start + insertion.length;
    }
  });
};
const addButton = () =>
  variant.value.buttons.push({ type: 'url', text: '', url: '' });
const changeButtonType = button => {
  if (button.type === 'url') {
    delete button.action;
    button.url = '';
  } else {
    delete button.url;
    button.action = '';
  }
};
const setWaba = event => {
  const source = wabaOptions.value.find(
    item => item.waba_template_id === event.target.value
  );
  if (!source) {
    delete variant.value.waba;
    return;
  }
  // Only an explicit user choice copies approved provider content.
  variant.value.body =
    source.channel_variants?.whatsapp?.body || source.body || '';
  variant.value.buttons = JSON.parse(
    JSON.stringify(
      source.channel_variants?.whatsapp?.buttons || source.buttons || []
    )
  );
  variant.value.waba = {
    template_id: source.waba_template_id,
    values: [...(source.waba_vars || [])],
    image_header: !!source.waba_has_image_header,
  };
};
const copyVariant = () => {
  if (!copyFrom.value) return;
  // eslint-disable-next-line no-alert
  if (
    !window.confirm(
      t('copyConfirm', { channel: channelLabel(activeChannel.value) })
    )
  )
    return;
  const value = JSON.parse(JSON.stringify(form.variants[copyFrom.value]));
  delete value.waba;
  if (activeChannel.value === 'sms') {
    value.buttons = [];
    value.image_url = '';
    value.ical = {};
  }
  form.variants[activeChannel.value] = value;
  form.enabled[activeChannel.value] = true;
};
const upload = async event => {
  const file = event.target.files?.[0];
  if (!file) return;
  const target = activeChannel.value;
  uploading.value = true;
  try {
    const data = new FormData();
    data.append('attachment', file);
    const response = await axios.post(
      `/api/v1/accounts/${route.params.accountId}/upload`,
      data
    );
    form.variants[target].image_url = response.data.file_url;
  } catch (failure) {
    error.value = describeError(failure);
  } finally {
    uploading.value = false;
    event.target.value = '';
  }
};
const toggleCalendar = event => {
  variant.value.ical = event.target.checked
    ? {
        enabled: true,
        title: t('calendarDefault'),
        description: '',
        alarm_minutes: 120,
      }
    : {};
};
const sendTest = async () => {
  if (!canSend.value || test.sending) return;
  // eslint-disable-next-line no-alert
  if (
    !window.confirm(
      t('testConfirm', {
        to: test.to,
        source: relevantInboxes.value.find(i => i.id === Number(test.inboxId))
          ?.name,
      })
    )
  )
    return;
  test.sending = true;
  test.result = '';
  try {
    const { data } = await TemplatesAPI.testSend(form.id, {
      channel: activeChannel.value,
      inbox_id: Number(test.inboxId),
      to: test.to.trim(),
      vars: previewVars(),
    });
    test.result = t('sent', { source: data.via });
  } catch (failure) {
    test.result = describeError(failure);
  } finally {
    test.sending = false;
  }
};
</script>

<template>
  <div
    class="flex flex-col w-full h-full min-h-0 overflow-hidden bg-n-surface-1 text-n-slate-12"
  >
    <TemplatePageHeader
      v-model:search="search"
      :title="editing ? (form.id ? t('edit') : t('create')) : t('title')"
      :show-search="!editing"
      :search-placeholder="t('search')"
    >
      <template v-if="editing">
        <span v-if="dirty" class="text-xs text-n-amber-11">{{
          t('unsaved')
        }}</span>
        <Button
          :label="t('back')"
          variant="ghost"
          color="slate"
          size="sm"
          icon="i-lucide-arrow-left"
          :disabled="saving || uploading"
          @click="back"
        />
        <Button
          :label="saving ? t('saving') : t('save')"
          size="sm"
          :disabled="saving || uploading"
          @click="save"
        />
      </template>
      <template v-else>
        <Button
          :label="t('sync')"
          variant="ghost"
          color="slate"
          size="sm"
          :disabled="loading"
          @click="sync"
        />
        <div class="w-px h-4 bg-n-strong" />
        <Button
          :label="t('create')"
          size="sm"
          icon="i-lucide-plus"
          :disabled="loading"
          @click="edit(null)"
        />
      </template>
    </TemplatePageHeader>
    <p
      v-if="error"
      role="alert"
      class="mx-6 mt-4 whitespace-pre-wrap text-sm text-n-ruby-11"
    >
      {{ error }}
    </p>
    <main v-if="!editing" class="flex-1 min-h-0 overflow-auto px-6 pb-6">
      <p v-if="loading">{{ t('loading') }}</p>
      <p
        v-else-if="!filteredTemplates.length"
        class="py-10 text-center text-sm text-n-slate-11"
      >
        {{ search ? t('noResults') : t('empty') }}
      </p>
      <div
        v-else
        class="overflow-x-auto border rounded-xl border-n-weak bg-n-solid-1"
      >
        <table class="w-full text-sm text-left border-collapse">
          <thead class="text-n-slate-11">
            <tr class="border-b border-n-weak">
              <th class="px-3 py-2.5 font-medium whitespace-nowrap">
                {{ t('name') }}
              </th>
              <th class="px-3 py-2.5 font-medium whitespace-nowrap">
                {{ t('category') }}
              </th>
              <th class="px-3 py-2.5 font-medium whitespace-nowrap">
                {{ t('variants') }}
              </th>
              <th class="px-3 py-2.5 font-medium whitespace-nowrap text-right">
                {{ t('actions') }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="item in filteredTemplates"
              :key="item.id"
              class="border-b border-n-weak last:border-0 hover:bg-n-alpha-1 cursor-pointer"
              @click="edit(item)"
            >
              <td class="px-3 py-2.5">
                <button
                  class="text-left font-medium text-n-slate-12 hover:underline"
                  @click.stop="edit(item)"
                >
                  {{ item.name }}
                </button>
              </td>
              <td class="px-3 py-2.5 text-n-slate-11">
                {{ item.category || '—' }}
              </td>
              <td class="px-3 py-2.5">
                <div class="flex flex-wrap gap-1">
                  <span
                    v-for="channel in item.configured_channels"
                    :key="channel"
                    class="px-2 py-0.5 text-xs rounded-md bg-n-alpha-2 text-n-slate-11"
                    >{{ channelLabel(channel) }}</span
                  >
                </div>
              </td>
              <td class="px-3 py-2.5 text-right whitespace-nowrap" @click.stop>
                <Button
                  :aria-label="t('edit')"
                  :title="t('edit')"
                  icon="i-lucide-pen-line"
                  color="slate"
                  variant="ghost"
                  size="sm"
                  @click="edit(item)"
                /><Button
                  :aria-label="t('delete')"
                  :title="t('delete')"
                  icon="i-lucide-trash-2"
                  color="slate"
                  variant="ghost"
                  size="sm"
                  @click="remove(item)"
                />
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </main>
    <main v-else class="flex-1 min-h-0 overflow-y-auto px-6 pb-6">
      <fieldset :disabled="saving || test.sending" class="min-w-0">
        <div class="grid gap-4 mb-6 md:grid-cols-2">
          <Input
            v-model="form.name"
            :label="t('name')"
            data-testid="template-name"
          />
          <Input v-model="form.category" :label="t('category')" />
        </div>
        <div
          role="tablist"
          :aria-label="t('variants')"
          class="flex flex-wrap gap-6 mb-6 border-b border-n-weak"
        >
          <button
            v-for="channel in CHANNELS"
            :id="'tab-' + channel.key"
            :key="channel.key"
            role="tab"
            :aria-selected="activeChannel === channel.key"
            :aria-controls="'variant-' + channel.key"
            class="px-1 py-3 border-b-2 text-sm transition-colors"
            :class="
              activeChannel === channel.key
                ? 'border-n-brand text-n-slate-12 font-medium'
                : 'border-transparent text-n-slate-11 hover:text-n-slate-12'
            "
            @click="activeChannel = channel.key"
          >
            {{ channel.label }}
            <span class="ml-2 text-xs">{{
              t(form.enabled[channel.key] ? 'configured' : 'notConfigured')
            }}</span>
          </button>
        </div>
        <div
          :id="'variant-' + activeChannel"
          role="tabpanel"
          :aria-labelledby="'tab-' + activeChannel"
          class="grid gap-6 xl:grid-cols-2"
        >
          <section
            class="min-w-0 p-5 border rounded-xl border-n-weak bg-n-solid-1"
          >
            <label class="flex items-center gap-3 text-sm font-medium"
              ><input v-model="form.enabled[activeChannel]" type="checkbox" />{{
                t('configure', { channel: channelLabel(activeChannel) })
              }}</label
            >
            <p class="mt-2 text-xs leading-5 text-n-slate-11">
              {{ t('independent') }}
            </p>
            <p
              v-if="!form.enabled[activeChannel]"
              class="mt-6 text-sm text-n-slate-11"
            >
              {{ t('missingHint') }}
            </p>
            <div v-else class="mt-5 space-y-5">
              <label
                v-if="activeChannel === 'whatsapp'"
                data-testid="waba-provider"
                class="block text-sm"
                >{{ t('waba') }}
                <select
                  :value="variant.waba?.template_id || ''"
                  class="w-full mt-2"
                  @change="setWaba"
                >
                  <option value="">{{ t('freeText') }}</option>
                  <option
                    v-for="item in wabaOptions"
                    :key="item.id"
                    :value="item.waba_template_id"
                  >
                    {{ item.name }}
                  </option>
                </select>
                <span class="block mt-1 text-xs text-n-slate-11">{{
                  t('wabaHint')
                }}</span>
              </label>
              <label class="block text-sm"
                >{{ t('body')
                }}<textarea
                  ref="bodyRef"
                  v-model="variant.body"
                  data-testid="variant-body"
                  rows="8"
                  class="w-full mt-2"
                  :readonly="!!variant.waba"
                />
              </label>
              <select
                v-if="!variant.waba"
                :aria-label="am('insertVariable')"
                class="w-full text-sm"
                value=""
                @change="
                  insertToken($event.target.value);
                  $event.target.value = '';
                "
              >
                <option value="" disabled>{{ am('insertVariable') }}</option>
                <optgroup
                  v-for="group in tokenGroups"
                  :key="group.key"
                  :label="am(group.key)"
                >
                  <option
                    v-for="token in group.tokens"
                    :key="token"
                    :value="token"
                  >
                    {{ token }}
                  </option>
                </optgroup>
              </select>
              <template v-if="variant.waba">
                <p class="text-xs text-n-slate-11">{{ t('wabaLocked') }}</p>
                <label
                  v-for="(_, index) in variant.waba.values"
                  :key="index"
                  class="block text-sm"
                  >{{ t('parameter', { number: index + 1 })
                  }}<input
                    v-model="variant.waba.values[index]"
                    class="w-full mt-1"
                /></label>
              </template>
              <PreparationEditor v-model="variant.preparation_rules" />
              <template v-if="activeChannel !== 'sms'">
                <label class="block text-sm"
                  >{{ t('image')
                  }}<input
                    v-model="variant.image_url"
                    class="w-full mt-2"
                    :placeholder="t('imagePlaceholder')"
                /></label>
                <label class="block text-xs text-n-slate-11"
                  >{{ uploading ? t('uploading') : t('upload')
                  }}<input
                    type="file"
                    accept="image/*"
                    :disabled="uploading"
                    class="block mt-2 w-full text-xs"
                    @change="upload"
                /></label>
                <div>
                  <h2 class="text-sm font-medium">{{ t('buttons') }}</h2>
                  <div
                    v-for="(button, index) in variant.buttons"
                    :key="index"
                    class="mt-3 p-3 border rounded-lg border-n-weak"
                  >
                    <div class="grid gap-2 sm:grid-cols-2">
                      <label class="text-xs"
                        >{{ t('buttonText')
                        }}<input
                          v-model="button.text"
                          :readonly="!!variant.waba"
                          class="w-full mt-1"
                      /></label>
                      <label class="text-xs"
                        >{{ t('buttonType')
                        }}<select
                          v-model="button.type"
                          :disabled="!!variant.waba"
                          class="w-full mt-1"
                          @change="changeButtonType(button)"
                        >
                          <option value="url">{{ t('urlButton') }}</option>
                          <option value="quick_reply">
                            {{ t('replyButton') }}
                          </option>
                        </select></label
                      >
                    </div>
                    <label
                      v-if="button.type === 'url'"
                      class="block mt-2 text-xs"
                      >{{ t('url')
                      }}<input
                        v-model="button.url"
                        :readonly="!!variant.waba"
                        class="w-full mt-1"
                    /></label>
                    <label
                      v-else-if="!variant.waba"
                      data-testid="button-action"
                      class="block mt-2 text-xs"
                      >{{ t('actionCode')
                      }}<input
                        v-model="button.action"
                        class="w-full mt-1"
                      /><span class="block mt-1 text-n-slate-11">{{
                        t('actionHint')
                      }}</span></label
                    >
                    <Button
                      v-if="!variant.waba"
                      :label="t('removeButton')"
                      variant="ghost"
                      size="sm"
                      class="mt-2"
                      @click="variant.buttons.splice(index, 1)"
                    />
                  </div>
                  <Button
                    v-if="!variant.waba"
                    :label="t('addButton')"
                    variant="ghost"
                    size="sm"
                    class="mt-2"
                    @click="addButton"
                  />
                </div>
                <div>
                  <label class="flex items-center gap-2 text-sm"
                    ><input
                      :checked="!!variant.ical.enabled"
                      type="checkbox"
                      @change="toggleCalendar"
                    />{{ t('calendar') }}</label
                  >
                  <div v-if="variant.ical.enabled" class="mt-3 space-y-3">
                    <label class="block text-xs"
                      >{{ t('calendarTitle')
                      }}<input
                        v-model="variant.ical.title"
                        :aria-label="t('calendarTitle')"
                        class="w-full mt-1"
                    /></label>
                    <label class="block text-xs"
                      >{{ t('description')
                      }}<textarea
                        v-model="variant.ical.description"
                        class="w-full mt-1"
                      />
                    </label>
                    <label class="block text-xs"
                      >{{ t('alarm')
                      }}<input
                        v-model.number="variant.ical.alarm_minutes"
                        type="number"
                        min="0"
                        step="1"
                        class="w-full mt-1"
                    /></label>
                  </div>
                </div>
              </template>
              <p v-else class="text-xs text-n-slate-11">{{ t('smsHint') }}</p>
            </div>
            <div class="flex flex-wrap gap-2 pt-4 mt-5 border-t border-n-weak">
              <select
                v-model="copyFrom"
                :aria-label="t('copyFrom')"
                class="flex-1 min-w-0 text-sm"
              >
                <option value="">{{ t('copyFrom') }}</option>
                <option
                  v-for="channel in CHANNELS.filter(
                    c => c.key !== activeChannel && form.enabled[c.key]
                  )"
                  :key="channel.key"
                  :value="channel.key"
                >
                  {{ channel.label }}
                </option>
              </select>
              <Button
                :label="t('copy')"
                variant="ghost"
                :disabled="!copyFrom"
                @click="copyVariant"
              />
            </div>
          </section>
          <section class="min-w-0 space-y-4">
            <div class="p-5 border rounded-xl border-n-weak bg-n-solid-1">
              <h2 class="text-sm font-semibold">
                {{ t('preview', { channel: channelLabel(activeChannel) }) }}
              </h2>
              <p class="mt-1 text-xs text-n-slate-11">{{ t('previewHint') }}</p>
              <label class="block my-3 text-xs"
                >{{ am('examples') }}
                <select v-model="example" class="w-full mt-1">
                  <option
                    v-for="item in ['single', 'moved', 'day', 'missing']"
                    :key="item"
                    :value="item"
                  >
                    {{ am(item) }}
                  </option>
                </select>
              </label>
              <div
                v-for="([key, rule], index) in preparationFields"
                :key="key"
                class="mb-3"
              >
                <label class="block text-xs"
                  >{{ am('sampleValue') }} · {{ index + 1 }} ·
                  {{ am(rule.context) }}
                  <input
                    :value="preparationSamples[key]?.[0] ?? 'ПО;ТО'"
                    class="w-full mt-1"
                    @input="
                      preparationSamples[key] = [
                        $event.target.value,
                        preparationSamples[key]?.[1] ?? 'ПО;ТО',
                      ]
                    "
                  />
                </label>
                <label
                  v-if="example === 'day' && rule.context !== 'patient'"
                  class="block mt-2 text-xs"
                  >{{ am('sampleValue2') }}
                  <input
                    :value="preparationSamples[key]?.[1] ?? 'ПО;ТО'"
                    class="w-full mt-1"
                    @input="
                      preparationSamples[key] = [
                        preparationSamples[key]?.[0] ?? 'ПО;ТО',
                        $event.target.value,
                      ]
                    "
                  />
                </label>
              </div>
              <p
                v-if="preview?.missing_variables?.length"
                role="alert"
                class="my-3 text-xs text-n-ruby-11"
              >
                {{ am('missingVariables') }}:
                {{ preview.missing_variables.join(', ') }}
              </p>
              <ChannelPhonePreview
                :channel="activeChannel"
                :preview="preview"
                :sender="vars['Филиал']"
                :time="vars['Приём.Время']"
                :loading="previewBusy"
                :error="previewError"
                :missing-message="t('missingHint')"
              />
              <template v-if="preview && !preview.error && !previewBusy">
                <p v-if="preview.sms" class="mt-3 text-xs text-n-slate-11">
                  {{ t('smsLength', { length: preview.sms.length }) }}
                </p>
                <p
                  v-for="warning in preview.warnings"
                  :key="warning"
                  class="mt-3 text-xs leading-5 text-n-amber-11"
                >
                  {{ t('warnings.' + warning) }}
                </p>
              </template>
            </div>
            <details class="p-5 border rounded-xl border-n-weak bg-n-solid-1">
              <summary class="text-sm cursor-pointer">
                {{ t('sampleData') }}
              </summary>
              <label
                v-for="(_, key) in vars"
                :key="key"
                class="block mt-3 text-xs"
                >{{ key
                }}<input
                  v-model="vars[key]"
                  :aria-label="key"
                  class="w-full mt-1"
              /></label>
            </details>
            <details class="p-5 border rounded-xl border-n-weak bg-n-solid-1">
              <summary class="text-sm cursor-pointer">{{ t('test') }}</summary>
              <p class="mt-3 text-xs text-n-amber-11">{{ t('testWarning') }}</p>
              <label class="block mt-3 text-xs"
                >{{ t('source')
                }}<select v-model="test.inboxId" class="w-full mt-1">
                  <option value="">{{ t('chooseSource') }}</option>
                  <option
                    v-for="inbox in relevantInboxes"
                    :key="inbox.id"
                    :value="inbox.id"
                  >
                    {{ inbox.name }}
                  </option>
                </select></label
              >
              <label class="block mt-3 text-xs"
                >{{ t('recipient')
                }}<input
                  v-model="test.to"
                  class="w-full mt-1"
                  autocomplete="off"
              /></label>
              <Button
                :label="t('sendTest')"
                :disabled="!canSend || test.sending"
                class="mt-3"
                @click="sendTest"
              />
              <p v-if="test.result" role="status" class="mt-3 text-xs">
                {{ test.result }}
              </p>
            </details>
          </section>
        </div>
      </fieldset>
    </main>
  </div>
</template>
